import d3 = require("d3");
import React = require("react");
import ReactFauxDOM = require("react-faux-dom");
import {StatisticsUtils} from "../../../components/Utils/statistics/StatisticsUtils";
import {DashboardContainer} from "../../DashboardContainer";
import "./style/pieChartWithExternalLabels.scss";
import {FormatLocaleNumber} from "../DashboardUtils";
import {v4 as uuid} from 'uuid';

export interface IconSize {
    width: number;
    height: number;
}

export interface PieChartWithLabelsRow {
    code: string;
    label: string;
    frequency: number;
    icon?: string;
    order?: number;
    percentage?: number;
    color?: string;
}

interface PieState {
}

export interface DonutOptions {
    activeDonut: boolean;
    showTotal?: boolean;
    totalSize?: string;
}

export interface LabelOptions {
    externalLegend: boolean; // if true items names and icons are shown in a legend and labels on pie sector show only values
}

export interface PieChartWithLabelsProps {
    width: number; // the width of the area where the chart will be insert
    height: number; // the height of the svg where the chart will be insert
    donutOptions: DonutOptions; // if true the chart will have a donut shape
    data: PieChartWithLabelsRow[];
    showPercentage: boolean; // if true the labels will contain the value in percentage
    showFrequency?: boolean; // if true the labels will contain the value in frequency (default behaviour is true)
    showIcon: boolean; // if true the labels will contains the icon instead that the name of the item
    iconSize?: IconSize; // the icon size to apply to the icons if showIcon is true
    noDataMessage: string; // the message to show when no data are available
    radiusRatio: number; // the ratio of radius compare the width of the area
    labelLineDefaultLength: number;
    startAngle?: number; // define to which angle data are placed the default is 0
    order?: string; // define how to order sector of the pie
    groupingThreshold?: number; // percentage threshold below that grouping items in a generic 'Other' item
    texts?: any;
    labelsOptions: LabelOptions;
    selectedItem?: string; // the guid of the selected item
    onPathOver: Function;
    onClick?: Function;
}

export class PieChartWithLabels extends React.Component<PieChartWithLabelsProps, PieState> {

    private static SELECTION_COLOR: string = "var(--vebox-gray-hover-light)";
    public pieDimensions: any = null;
    public svgElement;

    constructor(props: PieChartWithLabelsProps) {
        super(props);
        this.mouseOut = this.mouseOut.bind(this);
        this.mouseOver = this.mouseOver.bind(this);
        const labelMargin = 5;
        this.svgElement = React.createRef();

    }

    /**
     * Compute frequency of each element, group exceeding elements into one generic element called 'Others' and order data.
     * If the maxNumberItems is defined it is used to define the percentage threshold under that group elements,
     * otherwise elements to group can be passed on elementsToGroup param
     * @param data the original set of data
     * @param maxNumberItems the max number of items visible
     * @param orderType the type of order to apply desc|asc|custom
     * @param texts text for the generic elements
     * @param elementToGroup codes of elements to group into the generic element. If it is defined it is prior than the maxNumberItems
     */
    public static prepareData(data: PieChartWithLabelsRow[], maxNumberItems: number, orderType: string, texts: any,
                              elementToGroup: PieChartWithLabelsRow[]) {
        let preparedData = data;
        let groupingThreshold;
        // compute percentage
        const total = preparedData.map((item) => {
            return item.frequency;
        }).reduce((a, b) => {
            return a + b;
        }, 0);
        if (preparedData.length === 0) {
            return preparedData;
        }
        preparedData.forEach((d, i) => {
            d.percentage = StatisticsUtils.getPercentage(total, d.frequency);
        });

        // compute grouping threshold agree with the max number of items to show
        if (preparedData.length > maxNumberItems) {
            preparedData = preparedData.sort(
                (a, b) => StatisticsUtils.dataComparator("desc", a, b, "frequency", "order"),
            );
            groupingThreshold = preparedData[maxNumberItems - 1].percentage;
        }

        if (groupingThreshold == undefined && elementToGroup == null) {
            preparedData = preparedData.sort(
                (a, b) => StatisticsUtils.dataComparator(orderType, a, b, "frequency", "order"));
            return preparedData;
        }
        let underThresholdItems;
        if (elementToGroup != null) {
            underThresholdItems = preparedData.filter(
                (original) => elementToGroup.filter((etg) => original.code === etg.code).length !== 0,
            );
            groupingThreshold = Math.max.apply(Math, elementToGroup.map((i) => i.percentage));

            // remove element to group
            preparedData = preparedData.filter(
                (original) => elementToGroup.filter((etg) => original.code === etg.code).length === 0,
            );
        } else {
            underThresholdItems = preparedData.filter((e) => e.percentage <= groupingThreshold);
            // avoid to group all items if all are under the threshold
            if (preparedData.length - underThresholdItems.length + 1 === 1) {
                preparedData = preparedData.sort(
                    (a, b) => StatisticsUtils.dataComparator(orderType, a, b, "frequency", "order"),
                );
                return preparedData;
            }
            // remove items under threshold
            preparedData = preparedData.filter((e) => e.percentage > groupingThreshold);
        }

        // construct Other item
        const maxOrderRemainedItems = Math.max.apply(Math,
            preparedData.map((e) => {
                return (e.order == null ? 0 : e.order);
            }));

        const otherItem = {
            code: uuid(),
            label: texts.others + (groupingThreshold ? " (<=" + groupingThreshold.toFixed(1) + "%)" : ""),
            frequency: 0,
            percentage: 0,
            order: (maxOrderRemainedItems !== -Infinity ? maxOrderRemainedItems : undefined),
            color: "dimgrey",
        };

        underThresholdItems.forEach((e) => {
            otherItem.frequency += e.frequency;
            otherItem.percentage += e.percentage;
        });

        // order all item excluded the other item that has to be always the last
        preparedData = preparedData.sort(
            (a, b) => StatisticsUtils.dataComparator(orderType, a, b, "frequency", "order"),
        );
        // add Other item to data
        preparedData.push(otherItem);
        return preparedData;
    }

    public getRef = (svgElement) => this.svgElement = svgElement;

    // the arc to draw the pie
    public arc(): d3.Arc<any, any> {
        return d3.arc()
            .outerRadius(this.pieDimensions.radius)
            .innerRadius(this.pieDimensions.innerRadius);
    }

    // the arc to place the labels
    public labelLineArc() {
        return d3.arc()
            .innerRadius(this.pieDimensions.radius + this.props.labelLineDefaultLength)
            .outerRadius(this.pieDimensions.radius + this.props.labelLineDefaultLength);
    }

    /*
     * Given a data item of pie compute the coordinates of the default label anchor point.
     * that is in the middle of the label arc circumference on the sector the data item refers to
     */
    public computeLabelAnchorPoint(d) {
        const centroid = this.labelLineArc().centroid(d);
        const midAngle = Math.atan2(centroid[1], centroid[0]);
        const x = Math.cos(midAngle) * this.pieDimensions.labelRadius;
        const sign = x > 0 ? 1 : -1;
        const y = Math.sin(midAngle) * this.pieDimensions.labelRadius;
        return [x, y];
    }

    public checkIfShowLabelOnSector(d: any): boolean {
        const ropeLength = 2 * this.pieDimensions.radius * Math.sin(d.endAngle - d.startAngle);
        // FIXME: try to check dimension of the sector and not the value
        // if ((d.endAngle - d.startAngle < Math.PI)
        //     && (
        //         (this.props.showIcon && Math.abs(ropeLength) < this.props.iconSize.width) ||
        //         (!this.props.showIcon && Math.abs(ropeLength) < 15)
        //     )
        // ) {
        if (d.data.percentage < 5) {
            return false;
        } else {
            return true;
        }
    }

    /**
     * Draw the all elements of the pie chart into the given ReactFauxDom
     */
    public draw(div: ReactFauxDOM) {
        const labelMargin = 5;
        // prepare elements
        let svg = d3.select(div).select("svg");

        if (svg.node() == null) {
            svg = d3.select(div).append("svg")
                .attr("width", this.pieDimensions.width)
                .attr("height", this.pieDimensions.height)
                .attr("ref", this.svgElement);
        }

        let legendItems;
        let noData = this.props.data.length === 0 || this.props.data.filter(d => d.frequency != 0).length == 0;
        const pieSvg = svg.append("g")
            .attr("className", noData ? "pie-chart-widget--nodata" : "")
            .attr(
                "transform",
                "translate(" + (this.pieDimensions.width) / 2 + "," + (this.pieDimensions.height) / 2 + ")",
            );

        const slicesContainer = pieSvg.append("g")
            .attr("className", "pie-chart-widget__slices");
        const labelsContainer = pieSvg.append("g")
            .attr("className", "pie-chart-widget__labels");

        /// manage pie without data
        if (noData) {
            pieSvg.append("circle")
                .attr("r", this.pieDimensions.radius);
            pieSvg.append("text")
                .text(this.props.noDataMessage)
                .attr("font-size", "18px");
            return div.toReact();
        }

        if (this.props.donutOptions.activeDonut && this.props.donutOptions.showTotal) {
            const total = this.props.data.map((a) => a.frequency).reduce((a, b) => a + b);
            slicesContainer.append("text")
                .attr("text-anchor", "middle")
                .attr("dy", ".35em")
                .attr("font-size",
                    this.props.donutOptions?.totalSize ?? ((total >= 1000) ? "13px" : "18px"))
                .text(FormatLocaleNumber(total));
        }

        const startAngle = this.props.startAngle == null ? 0 : this.props.startAngle;
        const pie = d3.pie()
            .sort((a, b) => StatisticsUtils.dataComparator(this.props.order, a, b, "frequency", "order"))
            .value((d) => (d as any).frequency)
            .startAngle(startAngle)
            .endAngle(startAngle + Math.PI * 2);

        const slices = slicesContainer.selectAll("pie-chart-widget__slice")
            .data(pie(this.props.data as any))
            .enter()
            .append("g")
            .attr("class", "pie-chart-widget__slice");

        // for each slice append path
        slices.append("path")
            .style("opacity", ".8")
            .attr("data-id", (d) => {
                return DashboardContainer.codifyEntityName((d as any).data.label);
            })
            .attr("d", this.arc() as any)
            .style("fill", (d) => {
                return (d as any).data.color;
            })
            .style("cursor", "pointer")
            .on("click", this.onSliceClick)
            .on("mouseover", this.mouseOver)
            .on("mouseout", this.mouseOut);

        // define labels groups
        const _ref = this;
        const labelsG = labelsContainer.selectAll(".pie-chart-widget__label")
            .data(pie(this.props.data as any))
            .enter()
            .append("g")
            .attr("className", "pie-chart-widget__label")
            .attr("data-id", (d: any) => {
                return "labels" + DashboardContainer.codifyEntityName(d.data.label);
            })
            .attr("data-index", (d) => {
                return d.index;
            })
            .attr("data-show", (d) => {
                return _ref.checkIfShowLabelOnSector(d);
            })
            .attr("style", (d) => {
                if (_ref.checkIfShowLabelOnSector(d)) {
                    return "visibility: visible";
                } else {
                    return "visibility: hidden";
                }
            })
            .attr("transform", (d) => {
                const point = this.computeLabelAnchorPoint(d);
                return "translate(" + point[0] + "," + point[1] + ")";
            })
            .attr("data-x", (d) => {
                return this.computeLabelAnchorPoint(d)[0];
            })
            .attr("data-y", (d) => {
                return this.computeLabelAnchorPoint(d)[1];
            });

        const lines = labelsG.append("line")
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", function (d) {
                const centroid = _ref.arc().centroid(d);
                const midAngle = Math.atan2(centroid[1], centroid[0]);
                return centroid[0] - (this as any).parentNode.getAttribute("data-x");
            })
            .attr("y2", function (d) {
                const centroid = _ref.arc().centroid(d);
                const midAngle = Math.atan2(centroid[1], centroid[0]);
                return centroid[1] - (this as any).parentNode.getAttribute("data-y");
            })
            .attr("className", "pie-chart-widget__label__line")
            .attr("stroke", (d) => {
                return (d as any).data.color;
            })
            .on("mouseover", this.mouseOver);

        // if external legend is not present add image with the icon for each labels group
        if (!this.props.labelsOptions.externalLegend) {
            if (this.props.showIcon) {
                labelsG.append("image")
                    .attr("xlink:href", (d) => {
                        return "images/component-icons/iconPlant-" + (d as any).data.icon + "";
                    })
                    .attr("width", this.props.iconSize.width)
                    .attr("height", this.props.iconSize.height)
                    .attr("x", (d) => {
                        const centroid = this.arc().centroid(d),
                            midAngle = Math.atan2(centroid[1], centroid[0]);
                        const sign = Math.cos(midAngle) > 0 ? 1 : -1;
                        return sign < 0 ? -this.props.iconSize.width : 0;

                    })
                    .attr("y", -this.props.iconSize.height / 2);
            } else {
                // for each labels group add text with the label name
                labelsG.append("text")
                    .attr("x", (d) => {
                        const centroid = this.arc().centroid(d),
                            midAngle = Math.atan2(centroid[1], centroid[0]),
                            x = Math.cos(midAngle) * this.pieDimensions.labelRadius,
                            sign = x > 0 ? 1 : -1;
                        return sign < 0 ? -labelMargin : labelMargin;
                    })
                    .attr("text-anchor", (d) => {
                        const centroid = this.arc().centroid(d),
                            midAngle = Math.atan2(centroid[1], centroid[0]),
                            x = Math.cos(midAngle) * this.pieDimensions.labelRadius;
                        return x > 0 ? "start" : "end";
                    })
                    .attr("font-size", `${Math.max(14, this.pieDimensions.radius / 10)}px`)
                    .attr("className", "pie-chart-widget__label__name")
                    .attr("dy", ".35em")
                    .attr("data-label", (d) => {
                        return (d as any).data.label;
                    })
                    .text((d) => {
                        return (d as any).data.label;
                    });
            }
        }

        // for each labels group add text with the label value
        let valueDy = 1.55;
        if (this.props.labelsOptions.externalLegend || this.props.showIcon) {
            valueDy = 0.35;
        }

        labelsG.append("text")
            .attr("className", "pie-chart-widget__label__value")
            .attr("dy", valueDy + "em")
            .attr("font-size", `${Math.max(13, Math.floor(this.pieDimensions.radius / 10))}px`)
            .attr("text-anchor", (d) => {
                const centroid = this.arc().centroid(d),
                    midAngle = Math.atan2(centroid[1], centroid[0]),
                    x = Math.cos(midAngle) * this.pieDimensions.labelRadius;
                return (x > 0 || (d.index === this.props.data.length - 1 && (d as any).data.percentage < 10)) ? "start" : "end";
            })
            .attr("x", (d) => {
                const centroid = this.arc().centroid(d),
                    midAngle = Math.atan2(centroid[1], centroid[0]),
                    x = Math.cos(midAngle) * this.pieDimensions.labelRadius,
                    sign = x > 0 ? 1 : -1;
                return (sign < 0 && (d.index !== this.props.data.length - 1 || (d as any).data.percentage > 10)) ?
                    (-labelMargin - (this.props.showIcon ? this.props.iconSize.width : 0)) :
                    (((this.props.showIcon ? this.props.iconSize.width : 0) + labelMargin)
                    );
            })
            .text((d) => {
                let text = "";
                if (
                    this.props.showFrequency == null
                    || (this.props.showFrequency != null && this.props.showFrequency)
                ) {
                    text += FormatLocaleNumber((d as any).data.frequency);
                }
                if (!this.props.showIcon && this.props.showPercentage) {
                    let percentage;
                    if ((d as any).data.percentage == null) {
                        const total = this.props.data.map((item) => {
                            return item.frequency;
                        }).reduce((a, b) => {
                            return a + b;
                        }, 0);
                        percentage = StatisticsUtils.getPercentage(total, (d as any).data.frequency);
                    } else {
                        percentage = (d as any).data.percentage;
                    }

                    let value = FormatLocaleNumber(percentage, {toFixed: 1}) + "%";
                    if (percentage < 0.1 && percentage > 0) {
                        value = "< 0.1%";
                    }
                    let percentageLabel = " (" + value + ")";
                    if (this.props.showFrequency != null && !this.props.showFrequency) {
                        percentageLabel = value;
                    }
                    text += percentageLabel;
                }
                return text;
            });

        // for each labels group add text with percentage value
        if (this.props.showPercentage && this.props.showIcon) {
            let percentageDy = 2.55;
            if (this.props.showIcon || this.props.labelsOptions.externalLegend) {
                percentageDy = 1.35;
            }
            labelsG.append("text")
                .attr("className", "pie-chart-widget__label__percentage")
                .attr("dy", percentageDy + "em")
                .attr("x", (d) => {
                    const centroid = this.arc().centroid(d),
                        midAngle = Math.atan2(centroid[1], centroid[0]),
                        x = Math.cos(midAngle) * this.pieDimensions.labelRadius,
                        sign = x > 0 ? 1 : -1;
                    return sign < 0 ? -labelMargin - (this.props.showIcon ? this.props.iconSize.width : 0) : ((this.props.showIcon ? this.props.iconSize.width : 0) + labelMargin);
                })
                .text((d) => {
                        let percentage;
                        if ((d as any).data.percentage == null) {
                            const total = this.props.data.map((item) => {
                                return item.frequency;
                            }).reduce((a, b) => {
                                return a + b;
                            }, 0);
                            percentage = StatisticsUtils.getPercentage(total, (d as any).data.frequency);
                        } else {
                            percentage = (d as any).data.percentage;
                        }

                        let value = percentage.toFixed(1) + "%";
                        if (percentage < 0.1 && percentage > 0) {
                            value = "< 0.1%";
                        }
                        const label = "(" + value + ")";
                        return label;
                    },
                )
                .attr("text-anchor", (d) => {
                    const centroid = this.arc().centroid(d),
                        midAngle = Math.atan2(centroid[1], centroid[0]),
                        x = Math.cos(midAngle) * this.pieDimensions.labelRadius;
                    return x > 0 ? "start" : "end";
                })
                .attr("font-size", `${Math.max(13, this.pieDimensions.radius / 8)}px`);
        }
    }

    /**
     //Check if there are labels overlapped and move they to new position to avoid overlaps
     * */
    /**
     * Check if there are labels overlapped and move they to new position to avoid overlaps
     *
     * @param div the div element that contain the svg
     * @param spacing the distance to maintain between the origin y coordinate of two adjacent labels
     * @param alpha default adjusting distance to apply to avoid overlaps
     */
    public relaxLabels(div: ReactFauxDOM, spacing: number, alpha: number) {
        let again = false;
        let redraw = false;
        const svg = d3.select(div).select("svg");
        const labelGroups = svg.selectAll(".pie-chart-widget__label")
            .filter(function () {
                return (this as any).getAttribute("data-show") === "true";
            });
        const lines = svg.selectAll("line");
        const _ref = this;
        labelGroups.each(function (d, i) {
            const a = this;
            const da = d3.select(a),
                daText = da.select("text").node(),
                y1 = parseInt(da.attr("data-y"));

            labelGroups.each(function (d, j) {
                const b = this;
                if (a === b || j < i) {
                    return;
                }

                const db = d3.select(b);
                const dbText = db.select("text").node();
                if ((daText as any).getAttribute("text-anchor") !== (dbText as any).getAttribute("text-anchor")) {
                    return;
                }

                const y2 = parseInt(db.attr("data-y"));
                const deltaY = y1 - y2;

                if (Math.abs(deltaY) > spacing) {
                    return;
                }

                again = true;
                const sign = deltaY > 0 ? 1 : -1;
                let adjust = sign * alpha;
                let newDaY = y1 + adjust;
                if (newDaY < Math.sin((d as any).endAngle) * _ref.pieDimensions.radius) {
                    // if the new computed position move the element into the pie then don't use it but recompute
                    // a new greater adjust to apply to the other element
                    newDaY = y1;
                    adjust = (spacing - Math.abs(y2 - y1));
                }
                const newDbY = y2 - adjust;
                const overflowHeight = Math.abs(newDbY) - _ref.pieDimensions.height / 2;
                const minRadius = (_ref.pieDimensions.innerRadius === 0 ? _ref.pieDimensions.radius / 2 : _ref.pieDimensions.innerRadius);
                if (overflowHeight > 0 && _ref.pieDimensions.radius - overflowHeight > minRadius) {
                    // if the new computed position is above the top of the chart then reduce the radius of the pie and redraw it
                    _ref.pieDimensions.radius -= spacing;
                    _ref.pieDimensions.labelRadius -= spacing;
                    again = false;
                    redraw = true;
                } else {
                    da.attr("data-y", newDaY);
                    db.attr("data-y", newDbY);
                    da.attr("transform", "translate(" + da.attr("data-x") + "," + newDaY + ")");
                    db.attr("transform", "translate(" + db.attr("data-x") + "," + newDbY + ")");
                    db.select("line").attr("y2", function (d) {
                        return (parseFloat((this as any).getAttribute("y2")) + adjust);

                    });
                }
            });
        });

        if (again) {
            const labelElements = labelGroups.nodes();
            setTimeout(this.relaxLabels, 20);
        }
        if (redraw) {
            svg.selectAll("*").remove();
            this.draw(div);
            this.relaxLabels(div, spacing, alpha);
        }
    }

    // check if some labels exceed the width of the svg and in this case ellipsis it
    public ellipsisLabels(svg: SVGElement) {
        const _ref = this;
        d3.select(svg).selectAll(".pie-chart-widget__label")
            .each(function (d) {
                const _refLabelG = this;
                const labelX1Pos = (this as any).getBoundingClientRect().left; // WARNING: x field of BoundingClientRect can be undefined in some browser use left
                const labelX2Pos = (this as any).getBoundingClientRect().right;
                const textNodeTmp = d3.select(this).select(".pie-chart-widget__label__name").node();
                if (textNodeTmp != null) {
                    const textTmp = (textNodeTmp as any).getAttribute("data-label");
                }
                if (labelX1Pos < (svg as any).getBoundingClientRect().left ||
                    labelX2Pos > (svg as any).getBoundingClientRect().right) {
                    const textNode = d3.select(this).select(".pie-chart-widget__label__name").node();
                    if (textNode != null) {
                        const text = (textNode as any).getAttribute("data-label"); // WARNING: not use innerHTML don't work on IE
                        const newText = text.substring(0, Math.floor(text.length / 2)) + "...";

                        let labelEllipsis = d3.select(this).select(".pie-chart-widget__label__name--ellipsis")
                            .attr("style", function () {
                                    if ((_refLabelG as any).getAttribute("data-show") === "true") {
                                        return "visibility: visible";
                                    } else {
                                        return "visibility: hidden";
                                    }
                                },
                            );
                        if (labelEllipsis.size() === 0) {
                            labelEllipsis = d3.select(this).append("text")
                                .attr("class", "pie-chart-widget__label__name--ellipsis");
                        }
                        labelEllipsis.text(newText)
                            .attr("x", (textNode as any).getAttribute("x"))
                            .attr("dy", (textNode as any).getAttribute("dy"))
                            .attr("text-anchor", (textNode as any).getAttribute("text-anchor"))
                            .attr("font-size", (textNode as any).getAttribute("font-size"))
                            .on("mouseover", _ref.mouseOver)
                            .on("mouseout", _ref.mouseOut);
                        d3.select(textNode).attr("style", "visibility:hidden");
                        d3.select(_refLabelG).classed("ellipsis", true);
                    }
                }
            });
    }

    /**
     * hide the ellipsis label name element and show the original one if there is enough space to show it
     */
    public unEllipsisLabels(svg: SVGElement) {
        d3.select(svg).selectAll(".pie-chart-widget__label__name--ellipsis")
            .each(function (d) {
                const originalLabel = d3.select((this as any).parentNode).select(".pie-chart-widget__label__name");
                const labelX1Pos = (originalLabel.node() as any).getBoundingClientRect().left;
                const labelX2Pos = (originalLabel.node() as any).getBoundingClientRect().right;
                if (labelX1Pos >= (svg as any).getBoundingClientRect().left &&
                    labelX2Pos <= (svg as any).getBoundingClientRect().right) {
                    d3.select((this as any).parentNode).classed("ellipsis", false);
                    if ((originalLabel.node() as any).parentNode.getAttribute("data-show") === "true") {
                        originalLabel.attr("style", "visibility:visible");
                        d3.select(this).attr("style", "visibility:hidden");
                    }
                } else {
                    if ((originalLabel.node() as any).parentNode.getAttribute("data-show") === "true") {
                        originalLabel.attr("style", "visibility:hidden");
                        d3.select((this as any).parentNode).classed("ellipsis", true);
                        d3.select(this).attr("style", "visibility:visible");
                    }
                }
            });
    }

    /**
     * Show a tooltip with the given text
     * @param labelItem the group element of the label
     * @param text  the text to show into the tooltip
     */
    public handleLabelMouseOver(labelItem: SVGElement, text: string) {
        let tooltip = d3.select(labelItem).select(".pie-chart-widget__tooltip");
        if (tooltip.size() === 0) {
            const labelNameNode = d3.select(labelItem).select(".pie-chart-widget__label__name").node();
            const textWidthBBox = (d3.select(labelItem).select(".pie-chart-widget__label__name")
                .node() as any).getBBox();
            tooltip = d3.select(labelItem).append("g")
                .attr("class", "pie-chart-widget__tooltip")
                .attr("style", "visibility: visible");

            tooltip.append("rect")
                .attr("class", "pie-chart-widget__tooltip__rect")
                .attr("height", 20)
                .attr("width", textWidthBBox.width)
                .attr("fill", "white");

            tooltip.append("text")
                .attr("class", "pie-chart-widget__tooltip__text")
                .text(text)
                .attr("dy", "1.35em");

            // place tooltip in the opposite way respect to the anchor of the label to avoid overlaps
            const labelTextAnchor = (labelNameNode as any).getAttribute("text-anchor");
            if (labelTextAnchor === "start") {
                tooltip.attr("transform", "translate(" + (-textWidthBBox.width) + ",0)");
            }

        } else {
            tooltip.attr("style", "visibility: visible")
                .selectAll("*")
                .attr("style", "visibility: visible");
            tooltip.select("text")
                .text(text);
        }
    }

    /**
     * Hide all tooltips when mouse go out from a label element
     */
    public handleLabelMouseOut() {
        d3.select(this.svgElement.current).selectAll(".pie-chart-widget__tooltip")
            .attr("style", "visibility: hidden")
            .selectAll("*")
            .attr("style", "visibility: hidden");
    }

    public selectItem(div: ReactFauxDOM) {
        const svg = d3.select(div).select("svg");
        if (this.props.selectedItem != null) {
            this.hideAllLabels();
            svg.selectAll("[data-id=" + DashboardContainer.codifyEntityName(this.props.selectedItem) + "]")
                .style("fill", PieChartWithLabels.SELECTION_COLOR);
            const selectedItem = svg.selectAll(
                "[data-id=labels" + DashboardContainer.codifyEntityName(this.props.selectedItem) + "]",
            ).nodes();
            if (selectedItem.length !== 0) {
                const selectedItemIndex = parseInt((selectedItem[0] as any).getAttribute("data-index"));
                this.showSelectedLabel(selectedItemIndex);
            }
        } else {
            this.showLabels();
        }
    }

    public render() {
        // prepare elements
        const div = new ReactFauxDOM.Element("div");
        const radius = (Math.min(this.props.width, this.props.height) / this.props.radiusRatio) * 0.8 - 10;
        this.pieDimensions = {
            radius,
            width: this.props.width,
            height: this.props.height,
            labelRadius: radius + this.props.labelLineDefaultLength,
            innerRadius: this.props.donutOptions.activeDonut ? (Math.min(this.props.width,
                this.props.height) / this.props.radiusRatio * 0.4) : 0,
        };
        this.draw(div);

        // relax the label to avoid overlap
        const alpha = 0.5,
            spacing = (this.props.showIcon ? this.props.iconSize.height : 25);

        if (!this.props.labelsOptions.externalLegend) {
            this.relaxLabels(div, spacing, alpha);
        } else {
            this.selectItem(div);
        }

        return div.toReact();
    }

    public componentDidMount() {
        this.ellipsisLabels(this.svgElement.current);
    }

    public componentDidUpdate() {
        this.ellipsisLabels(this.svgElement.current);
        this.unEllipsisLabels(this.svgElement.current);
    }

    public hideAllLabels() {
        const labels = d3.select(this.svgElement.current).selectAll(".pie-chart-widget__label");

        // hide all labels and theirs children
        labels.attr("style", "visibility:hidden");
        labels.selectAll("*").attr("style", "visibility: hidden");

        labels.selectAll(".pie-chart-widget__tooltip")
            .selectAll("*")
            .attr("style", "visibility: hidden");
    }

    public showLabels() {
        const labels = d3.select(this.svgElement.current).selectAll(".pie-chart-widget__label");

        // show al labels to marked as to show and hide the other
        labels.attr("style", function () {
            if ((this as any).parentNode.getAttribute("data-show") === "true") {
                return "visibility:visible";
            } else {
                return "visibility:hidden";
            }
        });

        // show or hide all children elements of labels
        labels.selectAll("*")
            .attr("style", function () {
                if ((this as any).parentNode.getAttribute("data-show") === "true") {
                    return "visibility:visible";
                } else {
                    return "visibility:hidden";
                }
            });

        // show or hide name ellipsed of labels
        labels.selectAll(".pie-chart-widget__label__name--ellipsis")
            .attr("style", function () {
                if ((this as any).parentNode.getAttribute("class").indexOf("ellipsis") !== -1
                    && (this as any).parentNode.getAttribute("data-show") === "true"
                ) {
                    return "visibility: visible";
                } else {
                    return "visibility: hidden";
                }
            });

        // show or hide name not ellipsed of labels
        labels.selectAll(".pie-chart-widget__label__name")
            .attr("style", function () {
                if ((this as any).parentNode.getAttribute("class").indexOf("ellipsis") !== -1
                    || (this as any).parentNode.getAttribute("data-show") !== "true"
                ) {
                    return "visibility: hidden";
                } else {
                    return "visibility: visible";
                }
            });
    }

    public showSelectedLabel(selectedItemIndex: number) {
        const labels = d3.select(this.svgElement.current).selectAll(".pie-chart-widget__label");
        // show the current one with mouse over
        const selectedLabel = labels.filter(function (d) {
            return parseInt((this as any).getAttribute("data-index")) === selectedItemIndex;
        }).attr("style", "visibility:visible");

        // show all child except tooltip of the selected label group
        selectedLabel.selectAll("*")
            .filter(function () {
                return ((this as any).getAttribute("class") !== "pie-chart-widget__tooltip" &&
                    (this as any).getAttribute("class") !== "pie-chart-widget__tooltip__text" &&
                    (this as any).getAttribute("class") !== "pie-chart-widget__tooltip__rect");
            })
            .attr("style", "visibility:visible");

        // manage visibility of selected label name
        selectedLabel.selectAll(".pie-chart-widget__label__name--ellipsis")
            .attr("style", function () {
                if ((this as any).parentNode.getAttribute("class").indexOf("ellipsis") !== -1) {
                    return "visibility: visible";
                } else {
                    return "visibility: hidden";
                }
            });

        selectedLabel.selectAll(".pie-chart-widget__label__name")
            .attr("style", function () {
                if ((this as any).parentNode.getAttribute("class").indexOf("ellipsis") !== -1) {
                    return "visibility: hidden";
                } else {
                    return "visibility: visible";
                }
            });
    }

    public mouseOver(event, itemData) {
        if (this.props.labelsOptions.externalLegend) {
            this.props.onPathOver(itemData.data.label);
        } else {
            this.hideAllLabels();
            this.showSelectedLabel(itemData.index);
        }
    }

    public mouseOut() {
        if (this.props.labelsOptions.externalLegend) {
            this.props.onPathOver(null);
        } else {
            this.showLabels();
        }
    }

    private onSliceClick = (event, d) => {
        if (this.props.onClick) {
            this.props.onClick(d);
        }
    }
}